import React, { useState } from 'react';
import { Search, Loader2, AlertCircle } from 'lucide-react';
import { searchInsights } from '../lib/insights';

interface Insight {
  id: string;
  title: string;
  description: string;
  start_timestamp: string;
  end_timestamp: string;
  similarity: number;
  interview: {
    customer: {
      full_name: string;
    };
    project: {
      title: string;
    };
  };
}

interface InsightsSearchProps {
  projectId?: string;
  onInsightClick?: (insight: Insight) => void;
}

export function InsightsSearch({ projectId, onInsightClick }: InsightsSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const searchResults = await searchInsights(query, projectId);
      setResults(searchResults);
    } catch (error: any) {
      console.error('Search error:', error);
      setError(error.message || 'An error occurred while searching insights');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="relative">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder="Search insights..."
            className="block w-full rounded-lg border-gray-300 pl-10 focus:border-brand-orange focus:ring-brand-orange"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
        </div>
        <button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          className="absolute inset-y-0 right-0 px-4 py-2 bg-brand-orange text-white rounded-r-lg hover:bg-brand-orange-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            'Search'
          )}
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-brand-orange mx-auto" />
          <p className="mt-2 text-sm text-gray-500">Searching insights...</p>
        </div>
      )}

      {!loading && hasSearched && results.length === 0 && !error && (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No insights found matching your search.</p>
        </div>
      )}

      {results.length > 0 && (
        <div className="space-y-4">
          {results.map((insight) => (
            <div
              key={insight.id}
              onClick={() => onInsightClick?.(insight)}
              className={`bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:border-brand-orange transition-colors ${
                onInsightClick ? 'cursor-pointer' : ''
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-brand-teal">
                    {insight.title}
                  </h3>
                  {insight.interview && (
                    <div className="mt-1 text-sm text-gray-500">
                      <p>From: {insight.interview.customer.full_name}</p>
                      <p>Project: {insight.interview.project.title}</p>
                    </div>
                  )}
                </div>
                <span className="text-sm text-gray-400">
                  {Math.round(insight.similarity * 100)}% match
                </span>
              </div>
              {insight.description && (
                <p className="mt-2 text-sm text-gray-600">{insight.description}</p>
              )}
              {(insight.start_timestamp || insight.end_timestamp) && (
                <div className="mt-2 text-xs text-gray-400">
                  Time: {insight.start_timestamp || '0:00'} -{' '}
                  {insight.end_timestamp || 'end'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}